import {useGrpcRequest, useGrpcRequestCallback} from '@aboutyou/data-fetcher';
import {CampaignsService_GetDiscountsLazy} from '@aboutyou/grpc-clients/tadarida/aysa_api/services/campaigns/v1/service.grpc.lazy';
import {useHydrationDone} from '@aboutyou/lazy-imports';
import {useFullSession, useOptionalSession} from '@aboutyou/user';
import {useCallback, useEffect, useState} from 'react';
import {
  useTargetGroupIdentifier,
  useTargetGroupIdentifierCallback,
} from '../../../shared-deprecated/useTargetGroupIdentifier';
import {useGetDiscountsInMemoryCache} from './useGetDiscountsInMemoryCache/useGetDiscountsInMemoryCache';

/**
 * This request is only executed on the client side.
 * Use `useDiscountsRequest` if you want to render discount data on the server
 */
export const useDiscounts = () => {
  const isHydrationDone = useHydrationDone();
  const fullSession = useFullSession();
  const [disabled, setDisabled] = useState(true);

  const request = useDiscountsRequest({disabled});

  useEffect(() => {
    if (isHydrationDone && fullSession) {
      // eslint-disable-next-line local-rules/set-state-in-use-effect
      setDisabled(false);
    }
  }, [fullSession, isHydrationDone]);

  return request;
};

/**
 * GetDiscounts call creates a request waterfall on the server side, if you need the discount data only on the client side, use the `useDiscounts` hook instead.
 */
export const useDiscountsRequest = (
  {disabled}: {disabled?: boolean} = {disabled: false},
) => {
  const session = useOptionalSession();
  const targetGroup = useTargetGroupIdentifier();

  const {setCache, validateInMemoryData} = useGetDiscountsInMemoryCache();

  const discountResponse = useGrpcRequest(
    CampaignsService_GetDiscountsLazy,
    {
      session,
      targetGroup,
    },
    {
      // The campaign data is cached for 2.5 minutes, so polling every half that amount ensures we pick up updates
      refetchInterval: TWO_MINUTES_THIRTY_SECONDS,
      refetchOnWindowFocus: true,
      staleTime: TWO_MINUTES_THIRTY_SECONDS,
      nonEssential: true,
      disabled, // disable call if feature is not active. Mostly here because of basket
      onServerSuccess: setCache,
      validateInMemoryCacheRequest: validateInMemoryData,
      keepPreviousDataOnClientError: true,
    },
  );
  return discountResponse;
};

export const useDiscountsCallback = () => {
  const queryFn = useGrpcRequestCallback(CampaignsService_GetDiscountsLazy);
  const getTargetGroup = useTargetGroupIdentifierCallback();
  const session = useOptionalSession();

  return useCallback(() => {
    const targetGroup = getTargetGroup();
    return queryFn({session, targetGroup});
  }, [queryFn, session, getTargetGroup]);
};

const TWO_MINUTES_THIRTY_SECONDS = 2.5 * 60 * 1000;
